import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardBody, Container, Alert, Spinner, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap'
import DataTable, { TableColumn } from 'react-data-table-component'
import Loader from '../../../Components/Widgets/Loader/generic'
import { get, postJson } from '../../../hook/request'
import { toast } from 'react-toastify'
import { fmtDate } from '../../../Helpers'

type BollaRow = {
    bollaID: number
    codice: string
    data_bolla: string
    totale_prodotti: number
    totale_qta: number
    totale_scansionati: number
    chiuso: 0 | 1
}

function unwrapPayload(res: any) {
    return res?.data?.data ?? res?.data ?? res
}

export default function BolleLista() {
    const [list, setList] = useState<BollaRow[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirmRow, setConfirmRow] = useState<BollaRow | null>(null)
    const [workingId, setWorkingId] = useState<number | null>(null)

    const openConfirm = (row: BollaRow) => {
        setConfirmRow(row)
        setConfirmOpen(true)
    }

    const closeConfirm = () => {
        if (workingId != null) return
        setConfirmOpen(false)
        setConfirmRow(null)
    }



    const loadLista = async () => {
        setError(null)
        setLoading(true)
        try {
            const res = await get<any>('/bolla/list')
            const payload = unwrapPayload(res)

            const status = String(payload?.status ?? '').toLowerCase()
            const rows = Array.isArray(payload?.list)
                ? payload.list
                : Array.isArray(payload?.data)
                    ? payload.data
                    : Array.isArray(payload)
                        ? payload
                        : []

            if (status === 'ok' || Array.isArray(rows)) {
                setList(rows as BollaRow[])
            } else {
                setList([])
                setError(String(payload?.message ?? 'Impossibile caricare la lista').trim())
            }
        } catch (e: any) {
            const payload = unwrapPayload(e?.response)
            setList([])
            setError(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadLista()
    }, [])

    const doToggleBolla = async () => {
        if (!confirmRow) return

        const row = confirmRow
        const isClosed = row.chiuso === 1

        try {
            setWorkingId(row.bollaID)
            const url = isClosed
                ? `/bolle/apertura?ID=${encodeURIComponent(String(row.bollaID))}`
                : `/bolle/chiusura?ID=${encodeURIComponent(String(row.bollaID))}`

            const res = await postJson(url, {})
            const payload = unwrapPayload(res)

            const status = String(payload?.status ?? 'ok').toLowerCase()
            const message = String(payload?.message ?? (isClosed ? 'Bolla riaperta' : 'Bolla chiusa')).trim()

            if (status === 'ok') {
                toast.success(message || (isClosed ? 'Bolla riaperta' : 'Bolla chiusa'))
                setList(prev =>
                    prev.map(b => (b.bollaID === row.bollaID ? { ...b, chiuso: isClosed ? 0 : 1 } : b))
                )
                await loadLista()

                setConfirmOpen(false)
                setConfirmRow(null)
            } else {
                toast.error(message || 'Operazione non riuscita')
            }
        } catch (e: any) {
            const payload = unwrapPayload(e?.response)
            toast.error(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
        } finally {
            setWorkingId(null)
        }
    }


    const columns: TableColumn<BollaRow>[] = useMemo(
        () => [
            {
                name: 'Codice',
                selector: r => r.codice,
                sortable: true,
                wrap: true,
                minWidth: '160px',
            },
            {
                name: 'Data',
                selector: r => fmtDate(r.data_bolla),
                sortable: true,
                width: '130px',
            },
            {
                name: 'Prodotti',
                selector: r => Number(r.totale_prodotti) || 0,
                sortable: true,
                width: '110px',
                right: true,
            },
            {
                name: 'Quantità',
                selector: r => Number(r.totale_qta) || 0,
                sortable: true,
                width: '130px',
                right: true,
            },
            {
                name: 'Scansionati',
                selector: r => Number(r.totale_scansionati) || 0,
                sortable: true,
                width: '130px',
                right: true,
            },
            {
                name: 'Stato',
                cell: r =>
                    r.chiuso === 1 ? (
                        <Badge color="danger">Chiusa</Badge>
                    ) : (
                        <Badge color="success" style={{ color: 'black' }}>
                            Aperta
                        </Badge>
                    ),
                sortable: true,
                sortFunction: (a, b) => Number(a.chiuso) - Number(b.chiuso),
                width: '110px',
            },
            {
                name: 'Azioni',
                cell: row => {
                    const isClosed = row.chiuso === 1
                    const busy = workingId === row.bollaID

                    return (
                        <Button
                            size="sm"
                            color={isClosed ? 'success' : 'primary'}
                            outline
                            disabled={busy}
                            onClick={e => {
                                e.stopPropagation()
                                openConfirm(row)
                            }}
                        >
                            {busy ? <Spinner size="sm" /> : isClosed ? 'Apri' : 'Chiudi'}
                        </Button>
                    )
                },
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                width: '120px',
            },

        ],
        [workingId]
    )

    return (
        <>

            <Container fluid className="p-3">
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h3 className="mb-0">Lista Bolle</h3>
                            <Button color="secondary" outline size="sm" onClick={loadLista} disabled={loading}>
                                Aggiorna
                            </Button>
                        </div>

                        <div className="mt-3">
                            {error && (
                                <Alert color="danger" className="mb-3">
                                    {error}
                                </Alert>
                            )}

                            <DataTable<BollaRow>
                                data={list}
                                columns={columns}
                                keyField="bollaID"
                                progressPending={loading}
                                progressComponent={<Loader />}
                                pagination
                                paginationPerPage={50}
                                paginationRowsPerPageOptions={[50, 100, 200]}
                                striped
                                highlightOnHover
                                className="display"
                                noDataComponent={loading ? 'Caricamento…' : 'Nessuna bolla trovata.'}
                                conditionalRowStyles={[
                                    {
                                        when: row => row.chiuso === 1,
                                        style: { opacity: 0.75 },
                                    },
                                ]}
                            />
                        </div>
                    </CardBody>
                </Card>

            </Container>

            <Modal isOpen={confirmOpen} toggle={closeConfirm} centered>
                <ModalHeader toggle={closeConfirm}>
                    {confirmRow?.chiuso === 1 ? 'Riapri bolla' : 'Chiudi bolla'}
                </ModalHeader>

                <ModalBody>
                    {confirmRow && (
                        <>
                            <div className="mb-2">
                                <div style={{ fontWeight: 700 }}>{confirmRow.codice}</div>
                                <div className="text-muted" style={{ fontSize: 12 }}>
                                    Data: {fmtDate(confirmRow.data_bolla)}
                                </div>
                            </div>

                            <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.3 }}>
                                Prodotti: <strong>{confirmRow.totale_prodotti}</strong>
                                <br />
                                Tot Qta: <strong>{confirmRow.totale_qta}</strong>
                                <br />
                                Scansionati: <strong>{confirmRow.totale_scansionati}</strong>
                                <br />
                                Stato attuale: <strong>{confirmRow.chiuso === 1 ? 'CHIUSA' : 'APERTA'}</strong>
                            </div>

                            <Alert color={confirmRow.chiuso === 1 ? 'info' : 'warning'} className="mt-3 mb-0">
                                {confirmRow.chiuso === 1
                                    ? 'Confermando, la bolla verrà riaperta.'
                                    : 'Confermando, la bolla verrà chiusa.'}
                            </Alert>
                        </>
                    )}
                </ModalBody>

                <ModalFooter className="d-flex justify-content-between">
                    <Button color="light" style={{ color: 'black' }} onClick={closeConfirm} disabled={workingId != null}>
                        Annulla
                    </Button>

                    <Button color="primary" onClick={doToggleBolla} disabled={!confirmRow || workingId != null}>
                        {workingId != null ? <Spinner size="sm" /> : 'Conferma'}
                    </Button>
                </ModalFooter>
            </Modal>

        </>


    )
}
